import { FC, useState } from 'react';
import { Card, CardActionArea, CardContent, Box, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { IProjectUI } from 'data/types/project.types';
import ProjectCardActionsMenu from './ProjectCardActionsMenu';
import RenameProject from './RenameProject';
import RemoveProjectModal from './RemoveProjectModal';
import CardHeader from 'components/Base/Card/CardHeader';

type TProjectCardProps = {
  projectData: IProjectUI;
  onClick?: () => void;
};

const ProjectCard: FC<TProjectCardProps> = ({
  projectData,
  onClick = () => null,
}) => {
  const { name, id, color, description } = projectData;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openRenameProjectModal, setOpenRenameProjectModal] = useState(false);
  const [openRemoveProjectModal, setOpenRemoveProjectModal] = useState(false);

  const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const subheader =
    description === undefined ? 'Some useful description here' : description;

  const projectColor = color || '#3B82F6';

  return (
    <>
      <Card
        sx={{
          height: '100%',
          minHeight: 140,
          borderRadius: 3,
          border: 'none',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: projectColor,
          },
        }}
      >
        <CardActionArea
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
          onClick={onClick}
        >
          <CardContent
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: 2.5,
              pt: 3,
              '&:last-child': { pb: 2.5 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                width: '100%',
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: `${projectColor}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FolderIcon
                  sx={{
                    color: projectColor,
                    fontSize: 24,
                  }}
                />
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1rem',
                lineHeight: 1.3,
                mb: 0.75,
                color: 'text.primary',
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8125rem',
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {subheader}
            </Typography>
          </CardContent>
        </CardActionArea>
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 8,
          }}
          onClick={e => e.stopPropagation()}
        >
          <CardHeader
            sx={{
              p: 0,
              '& .MuiCardHeader-action': {
                m: 0,
              },
            }}
            hasaction={{
              onActionClick: onOpenMenu,
            }}
          />
        </Box>
      </Card>
      <ProjectCardActionsMenu
        anchorEl={menuAnchorEl}
        setAnchorEl={setMenuAnchorEl}
        setOpenRenameProjectModal={setOpenRenameProjectModal}
        setOpenRemoveProjectModal={setOpenRemoveProjectModal}
      />
      <RenameProject
        projectData={projectData}
        open={openRenameProjectModal}
        setOpen={setOpenRenameProjectModal}
      />
      <RemoveProjectModal
        open={openRemoveProjectModal}
        setOpen={setOpenRemoveProjectModal}
        projectId={id}
      />
    </>
  );
};

export default ProjectCard;
